import { useEffect, useState } from 'react';

const BREAKPOINTS = {
  xs: 375,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1400,
};

type BreakpointState = Record<keyof typeof BREAKPOINTS, boolean>;
type InitialBreakpoints = keyof typeof BREAKPOINTS | '_initial';
type Orientation = 'portrait' | 'landscape' | null;
type ResponsiveState = {
  greaterThan: BreakpointState;
  lessThan: BreakpointState;
  orientation: Orientation;
  mediaType: InitialBreakpoints;
};

export const useResponsive = (): ResponsiveState => {
  const getMediaQuery = (size: number, greater: boolean): string => {
    return greater ? `(min-width: ${size}px)` : `(max-width: ${size - 1}px)`;
  };

  const getMatches = (query: string) => {
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  };

  const createResponsiveObject = () => {
    const initialBreakpointState: BreakpointState = {
      xs: false,
      sm: false,
      md: false,
      lg: false,
      xl: false,
      xxl: false,
    };

    const greaterThan = { ...initialBreakpointState };
    const lessThan = { ...initialBreakpointState };

    Object.entries(BREAKPOINTS).forEach(([key, size]) => {
      greaterThan[key as keyof typeof BREAKPOINTS] = getMatches(getMediaQuery(size, true));
      lessThan[key as keyof typeof BREAKPOINTS] = getMatches(getMediaQuery(size, false));
    });

    const orientation: Orientation = getMatches('(orientation: portrait)')
      ? 'portrait'
      : getMatches('(orientation: landscape)')
        ? 'landscape'
        : null;

    const mediaType = (Object.keys(greaterThan).findLast((key) => greaterThan[key as keyof typeof greaterThan]) ||
      '_initial') as InitialBreakpoints;

    return { greaterThan, lessThan, orientation, mediaType };
  };

  const [responsive, setResponsive] = useState(createResponsiveObject());

  useEffect(() => {
    const handleChange = () => setResponsive(createResponsiveObject());

    window.addEventListener('resize', handleChange);

    return () => window.removeEventListener('resize', handleChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return responsive;
};
